import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import DateControl from '../../../components/Fields/DateControl';
import Field from '../../../components/Fields/Field';
import FieldLabel from '../../../components/Fields/FieldLabel';
import MultiSelectField from '../../../components/Fields/MultiSelectField';
import NumberField from '../../../components/Fields/NumberField';
import SelectListField from '../../../components/Fields/SelectListField';
import SwitchField from '../../../components/Fields/SwitchField';
import TextboxField from '../../../components/Fields/TextboxField';
import { FormConfig } from '../../../components/Fields/Types';
import useSelectListOptions from '../../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import AttachmentTypeModel from '../../../models/AttachmentTypeModel';
import { Result } from '../../../types/Result';

interface IProps {
    formData: AttachmentTypeModel
    serverErrors?: Result
    submitCount: number
    handleSubmit: () => void
}

const AttachmentTypeControl: React.FC<IProps> = ({ formData, serverErrors, submitCount, handleSubmit }) => {
    const dataSourceTypes = useSelectListOptions('api/v1/selectlist/DataSourceTypes', '-- Select datasource type');
    const businessPartnerTypes = useSelectListOptions('api/v1/selectlist/BusinessPartnerTypes');
    const itemTypes = useSelectListOptions('api/v1/selectlist/ItemTypes');
    const projectTypes = useSelectListOptions('api/v1/selectlist/ProjectTypes');

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'abbreviation', label: 'Abbreviation', validationRules: [{ type: 'isMaxLength', maxLength: 10 }] },
            { name: 'maxAttachments', label: 'MaxAttachments' },
            { name: 'imagePriority', label: 'Image priority' },
            { name: 'dataSourceTypeId', label: 'Datasource' },
            { name: 'businessPartnerTypeIds', label: 'Business-partner types' },
            { name: 'itemTypeIds', label: 'Item types' },
            { name: 'projectTypeIds', label: 'Project types' },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },

            { name: 'isGeneratable', label: 'Generatable', labelSize: 4 },
            { name: 'isSearchable', label: 'Searchable', labelSize: 4 },
            { name: 'canDelete', label: 'Attachment can be deleted', labelSize: 4 },
            { name: 'forLicense', label: 'For licenses', labelSize: 4 },
            { name: 'forUser', label: 'For users', labelSize: 4 },
            { name: 'forBom', label: 'For Bill-of-Materials', labelSize: 4 },

        ],
        handleSubmit: handleSubmit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps } = useValidation(formConfig, formData, serverErrors, submitCount);

    return (
        <Container fluid>
            <Row>
                <Col md='6'>
                    <TextboxField {...getFieldProps('name')} />
                    <TextboxField {...getFieldProps('abbreviation')} />
                    <NumberField  {...getFieldProps('maxAttachments')} />
                    <NumberField  {...getFieldProps('imagePriority')} />
                    <SelectListField {...getFieldProps('dataSourceTypeId')} options={dataSourceTypes} />
                    <MultiSelectField {...getFieldProps('itemTypeIds')} options={itemTypes} />
                    <MultiSelectField {...getFieldProps('projectTypeIds')} options={projectTypes} />
                    <MultiSelectField {...getFieldProps('businessPartnerTypeIds')} options={businessPartnerTypes} />
                    <Field {...getLabelProps('beginDate')}>
                        <DateControl {...getControlProps('beginDate')} />
                        <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                        <DateControl {...getControlProps('endDate')} />
                    </Field>
                </Col>
                <Col md='6'>
                    <SwitchField {...getFieldProps('isGeneratable')} />
                    <SwitchField {...getFieldProps('isSearchable')} />
                    <SwitchField {...getFieldProps('canDelete')} />
                    <SwitchField {...getFieldProps('forLicense')} />
                    <SwitchField {...getFieldProps('forUser')} />
                    <SwitchField {...getFieldProps('forBom')} />
                </Col>
            </Row>
        </Container>
    );
}
export default AttachmentTypeControl;